import { useMemo, useState } from 'react';
import useTokenIntelligenceData from './useTokenIntelligenceData';
import {
  deriveNarrativeSummary,
  deriveSignalStripMetrics,
  deriveTokenIntelligenceScore,
  deriveWhaleActivity,
} from './tokenIntelligenceDerived';

function buildFallbackActivity(data, whale) {
  const storyRows = Array.isArray(data && data.stories && data.stories.stories) ? data.stories.stories : [];
  const thesisRows = Array.isArray(data && data.theses && data.theses.theses) ? data.theses.theses : [];

  return {
    liquidity: data && data.liquidity ? data.liquidity : { latest: null, points: [] },
    largestTransfers: storyRows.slice(0, 3).map((story, index) => ({
      tx_hash: story && story.id ? `story-${story.id}` : `story-${index}`,
      summary: story && story.title ? story.title : whale && whale.headline ? whale.headline : 'Narrative-linked transfer signal',
      ts: story && story.ts_created ? story.ts_created : '',
      amount_usd: whale && whale.metrics ? whale.metrics.maxTransferUsd : 0,
    })),
    smartWalletActivity: thesisRows.slice(0, 3).map((thesis, index) => ({
      wallet: thesis && thesis.entity_address ? thesis.entity_address : `0xsmart${index}`,
      action: thesis && thesis.direction ? thesis.direction : 'watching',
      amount_usd: thesis && thesis.market_cap_at_creation ? thesis.market_cap_at_creation : 0,
      ts: thesis && thesis.ts_updated ? thesis.ts_updated : thesis && thesis.ts_created ? thesis.ts_created : '',
      label: thesis && thesis.entity_symbol ? thesis.entity_symbol : 'Related thesis',
      summary: thesis && thesis.thesis_text ? thesis.thesis_text : '',
    })),
    bridgeFlows: whale && whale.score >= 40 ? [{
      bridge: 'intelligence',
      direction: whale.band === 'high' ? 'clustered' : 'watch',
      amount_usd: whale.metrics ? whale.metrics.totalTransferUsd : 0,
      ts: storyRows[0] && storyRows[0].ts_created ? storyRows[0].ts_created : '',
      summary: whale.summary,
    }] : [],
  };
}
 
export default function useTokenIntelligenceViewModel(address) {
  const [chartRange, setChartRange] = useState('24h');
  const [chartMetric, setChartMetric] = useState('price');

  const query = useTokenIntelligenceData(address, {
    range: chartRange,
  });

  const { data, loading, errors } = query;

  const seedWhale = useMemo(() => deriveWhaleActivity(data), [data]);

  const fallbackActivity = useMemo(
    () => buildFallbackActivity(data, seedWhale),
    [data, seedWhale]
  );

  const intelligence = useMemo(() => {
    const base = { ...data, activity: fallbackActivity };
    const whale = deriveWhaleActivity(base);
    const score = deriveTokenIntelligenceScore(base);
    const narrative = deriveNarrativeSummary(base);
    const signals = deriveSignalStripMetrics(base);
    return { whale, score, narrative, signals };
  }, [data, fallbackActivity]);

  const chartLoading = chartMetric === 'liquidity' ? loading.liquidity : loading.priceHistory;
  const chartError = chartMetric === 'liquidity' ? errors.liquidity : errors.priceHistory;

  const panelLoading = useMemo(() => ({
    signalStrip: loading.metadata || loading.liquidity || loading.stories || loading.theses || loading.priceHistory,
    ai: loading.metadata || loading.stories || loading.theses,
    marketMetrics: loading.metadata || loading.liquidity,
    onChain: loading.stories || loading.theses || loading.liquidity,
    score: loading.metadata || loading.liquidity || loading.stories || loading.theses,
    narrativeFlow: loading.narrativeFlow || loading.metadata,
  }), [loading]);

  const panelErrors = useMemo(() => ({
    signalStrip: errors.metadata || errors.liquidity || errors.stories || errors.theses || errors.priceHistory,
    ai: errors.metadata || errors.stories || errors.theses,
    marketMetrics: errors.metadata || errors.liquidity,
    onChain: errors.stories || errors.theses,
    score: errors.metadata || errors.liquidity || errors.stories || errors.theses,
    narrativeFlow: errors.narrativeFlow,
  }), [errors]);

  return {
    ...query,
    chartRange,
    setChartRange,
    chartMetric,
    setChartMetric,
    chartLoading,
    chartError,
    fallbackActivity,
    intelligence,
    panelLoading,
    panelErrors,
  };
}
